// Records the last 3 stroke curves every 500m (boat accel or erg force), drives
// the Demo generator and attaches the PM5 force-curve listener. Refs only.
import { useCallback, useEffect, useRef } from 'react';
import { getCompletedStrokes, compactStroke, resetCurves, startDemo, stopDemo } from '@/lib/strokeCurveBuffer';
import { attachForceCurve } from '@/lib/pm5ForceCurve';
import { getPM5Connection } from '@/lib/pm5Connection';
import { activePM5 } from '@/lib/pm5StrokePower';

const SNAPSHOT_EVERY_M = 500;
const MAX_SNAPSHOTS = 200;

export default function useCurveSnapshots({ sessionActive, running, distance, kind, demo, strokeRate, pm5Device }) {
  const snapsRef = useRef([]);
  const lastBucketRef = useRef(0);
  const rateRef = useRef(24);
  if (strokeRate > 0) rateRef.current = rateRef.current * 0.9 + strokeRate * 0.1;

  useEffect(() => {
    if (!sessionActive) return;
    snapsRef.current = [];
    lastBucketRef.current = 0;
    resetCurves();
  }, [sessionActive]);

  useEffect(() => {
    if (!sessionActive) return;
    const bucket = Math.floor((distance || 0) / SNAPSHOT_EVERY_M);
    if (bucket <= lastBucketRef.current) return;
    lastBucketRef.current = bucket;
    const strokes = getCompletedStrokes(kind).slice(-3);
    if (!strokes.length || snapsRef.current.length >= MAX_SNAPSHOTS) return;
    snapsRef.current.push({
      distance_m: bucket * SNAPSHOT_EVERY_M,
      kind,
      strokes: strokes.map((s) => compactStroke(s)),
    });
  }, [distance, sessionActive, kind]);

  useEffect(() => {
    if (!demo || !running || pm5Device) return undefined;
    startDemo(kind, () => rateRef.current);
    return () => stopDemo();
  }, [demo, running, kind, pm5Device]);

  useEffect(() => {
    if (!pm5Device) return undefined;
    let detach = () => {};
    let cancelled = false;
    attachForceCurve(activePM5(getPM5Connection())).then((fn) => { if (cancelled) fn(); else detach = fn; });
    return () => { cancelled = true; detach(); };
  }, [pm5Device]);

  return useCallback(() => snapsRef.current, []);
}
